(() => {
  const recommendationsRoot = '[data-product-recommendations-root]';
  const productCarousel = '[data-product-carousel]';
  const loadScriptOnce = url => window.theme.loadScriptOnce('card-carousel', url);

  class RecommendedProducts extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
      this.controller = null;
      this.variantId = null;
      this.renderedVariantId = null;
      this.onReady = () => this.init();
      this.onVariant = event => {
        if (!this.dataset.merchSource) return;
        const id = String(event.detail?.variantId || '');
        const source = this.readSource();
        if (!source?.variants?.some(variant => variant.id === id)) return;
        this.variantId = id;
        if (this.renderMerchandising()) this.enhance();
      };
      this.onHistory = () => {
        this.variantId = null;
        if (this.renderMerchandising()) this.enhance();
      };
    }

    connectedCallback() {
      document.addEventListener('product:variant-dom-updated', this.onVariant);
      window.addEventListener('popstate', this.onHistory);
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', this.onReady, { once: true });
      else this.init();
    }

    init() {
      if (this.querySelector(productCarousel)) {
        this.removeAttribute('aria-busy');
        this.enhance();
        return;
      }
      const url = this.dataset.url;
      if (!url) return;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.disconnectObserver();
          this.fetchRecommendations(url);
        }, { rootMargin: '0px 0px 200px 0px' });
        this.observer.observe(this);
      } else this.fetchRecommendations(url);
    }

    disconnectObserver() {
      this.observer?.disconnect();
      this.observer = null;
    }

    readSource() {
      try { return JSON.parse(this.dataset.merchSource || 'null'); }
      catch { return null; }
    }

    renderMerchandising() {
      const output = this.querySelector('[data-merch-output]');
      const fallback = this.querySelector('template[data-merch-fallback]');
      if (!output || !fallback) return false;
      const source = this.readSource();
      const requestedId = this.variantId || new URL(window.location.href).searchParams.get('variant') || this.dataset.merchInitialVariant;
      if (requestedId === this.renderedVariantId && output.childElementCount) return false;
      const selected = source?.variants?.find(variant => variant.id === String(requestedId));
      const neededSize = source?.sizeIndex >= 0 ? selected?.options?.[source.sizeIndex] : null;
      const knownSize = Boolean(neededSize) || (source?.sizeIndex === -1 && source.variants.length === 1);
      const alternatives = this.querySelector('template[data-merch-alternatives]');
      let fragment;
      let count = 0;
      if (source?.complete === true && selected && knownSize && alternatives &&
          (Number(this.dataset.merchMissingSizes) > 2 || selected.available === false)) {
        try {
          const candidates = JSON.parse(alternatives.dataset.merchCandidates || '[]');
          const byId = new Map(candidates.map(candidate => [candidate.id, candidate]));
          fragment = alternatives.content.cloneNode(true);
          const wrapper = fragment.querySelector('[data-product-carousel] [data-carousel] > .swiper-wrapper');
          for (const slide of [...(wrapper?.children || [])]) {
            const card = slide.querySelector('[data-merch-product]');
            const candidate = byId.get(card?.dataset.merchProduct);
            const variant = candidate?.complete === true && candidate.variants.find(item =>
              item.available === true && (!neededSize || (candidate.sizeIndex >= 0 &&
                String(item.options[candidate.sizeIndex]).trim().toLowerCase() === String(neededSize).trim().toLowerCase())));
            if (!variant || count >= 4) { slide.remove(); continue; }
            const destination = new URL(candidate.url, window.location.href);
            if (destination.origin !== window.location.origin) { slide.remove(); continue; }
            destination.searchParams.set('variant', variant.id);
            card.dataset.merchItemId = 'shopify_ZZ_' + candidate.id + '_' + variant.id;
            card.dataset.merchUrl = destination.pathname + destination.search + destination.hash;
            // Deferred secondary images read this URL when their links are created.
            if (card.dataset.productUrl) {
              const deferredUrl = new URL(card.dataset.productUrl, window.location.href);
              if (deferredUrl.origin === destination.origin && deferredUrl.pathname === destination.pathname) {
                deferredUrl.searchParams.set('variant', variant.id);
                card.dataset.productUrl = deferredUrl.pathname + deferredUrl.search + deferredUrl.hash;
              }
            }
            for (const link of card.querySelectorAll('a[href]')) {
              const url = new URL(link.getAttribute('href'), window.location.href);
              if (url.origin === destination.origin && url.pathname === destination.pathname) {
                url.searchParams.set('variant', variant.id);
                link.setAttribute('href', url.pathname + url.search + url.hash);
              }
            }
            count++;
          }
        } catch { count = 0; }
      }
      if (!count) {
        fragment = fallback.content.cloneNode(true);
        const pairedIds = new Set([...document.querySelectorAll('[data-recommendation-kind="complete_the_look"] [data-merch-product]')].map(card => card.dataset.merchProduct));
        const carousel = fragment.querySelector(productCarousel);
        const wrapper = carousel?.querySelector('[data-carousel] > .swiper-wrapper');
        for (const slide of [...(wrapper?.children || [])]) {
          if (pairedIds.has(slide.querySelector('[data-merch-product]')?.dataset.merchProduct)) slide.remove();
        }
        if (wrapper && !wrapper.children.length) carousel.remove();
      }
      const hasProducts = Boolean(fragment.querySelector(productCarousel));
      output.replaceChildren(fragment);
      this.dataset.merchRowKind = count ? 'alternatives' : (hasProducts ? 'fallback' : 'empty');
      this.dataset.merchSelectedSize = neededSize || '';
      this.renderedVariantId = requestedId;
      const section = this.closest('.shopify-section');
      if (section) section.style.display = hasProducts ? '' : 'none';
      return hasProducts;
    }

    async enhance() {
      try {
        if (!this.querySelector(productCarousel)) return;
        await loadScriptOnce(this.dataset.carouselScript);
        if (!this.isConnected) return;
        if (window.Alpine && typeof window.Alpine.initTree === 'function') {
          window.Alpine.initTree(this.querySelector('[data-merch-output]') || this);
        }
      } catch (error) { console.error(error); }
    }

    hideSection() {
      this.innerHTML = '';
      this.removeAttribute('aria-busy');
      const section = this.closest('.shopify-section');
      if (section) section.style.display = 'none';
    }

    async fetchRecommendations(url) {
      const controller = new AbortController();
      this.controller = controller;
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Recommendations request failed: ' + response.status);
        const html = await response.text();
        if (!this.isConnected || controller.signal.aborted) return;
        const recommendation = new DOMParser().parseFromString(html, 'text/html').querySelector(recommendationsRoot);
        if (!recommendation || !recommendation.innerHTML.trim().length) { this.hideSection(); return; }
        this.innerHTML = recommendation.innerHTML;
        this.renderedVariantId = null;
        this.renderMerchandising();
        this.removeAttribute('aria-busy');
        await this.enhance();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.hideSection();
        console.error(error);
      } finally {
        if (this.controller === controller) this.controller = null;
      }
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.onReady);
      document.removeEventListener('product:variant-dom-updated', this.onVariant);
      window.removeEventListener('popstate', this.onHistory);
      this.disconnectObserver();
      this.controller?.abort();
    }
  }

  if (!customElements.get('recommended-products')) customElements.define('recommended-products', RecommendedProducts);
})();


